import { User } from '@auth0/auth0-react';

/**
 * Fun Typescript trick to get the type to conform to whatever
 * you need for your specific platform (web, react-native, etc),
 * while keeping its original shape. This is generally useful for situations like
 * React Native where we are handling the user authentication ever so differently and need
 * more control over the state of the user. This will be the general API shares by other packages,
 * but the app has it's own things going on. However, we are not allowed to adjust certain things
 * that are used globally. Suche as `getIdToken` or `getAccessToken`.
 */

// Declare the type of the default object
export declare type AuthContextPseudoState = {
  login: (passedOptions?: Record<string, unknown>) => void;
  signup: () => void;
  getIdToken: () => Promise<string | undefined>;
  getAccessToken: () => Promise<string | undefined>;
  logout: (passedOptions?: Record<string, unknown>) => void;
  isAuthenticated: boolean;
  getPermissions: () => Promise<UserPermissions[]>;
  isLoading?: boolean;
  user?: User;
};

// This will be the inherited type that we can manipulate
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AuthCustomContext {}

// Overwrite the values and keep the new typings local to the project using the auth package, do not change universally handled
// items for consistency across projects. Luckily, these params don't really need to change, just their implementations.
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AuthContextState extends AuthCustomContext, Omit<AuthContextPseudoState, keyof AuthCustomContext> {}

/**
 * This is not how we should actually be computing user permissions but for a super simple model, this works for now.
 */
export enum UserPermissions {
  ALL = 'abacus:*',
  EARLY_ACCESS_TRADE = 'abacus:early_access_trade:write',
  REFERRAL_BETA = 'referrals:beta',
}

export interface DecodedJWT {
  permissions: UserPermissions[];
}
